import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';

import { forumSavedPostsQueryOptions } from '~/api/forum/useForumSavedPosts';
import { forumUserPostsQueryOptions } from '~/api/forum/useForumUserPosts';
import { useUser } from '~/api/user';
import { Dialog, Header, Tabs } from '~/components';
import { productFruitsIds } from '~/constants/productFruits';
import { EnableCommunityProfileDialog } from '~/features/community';
import { routes } from '~/providers/RouterProvider/router.routes';

import { ForumHeaderPortal } from '../components/ForumHeaderPortal';
import { CreateForumPostDialog } from '../CreateForumPostDialog/CreateForumPostDialog';
import { wrapperStyle } from './ForumOverviewLayout.css';

export const ForumOverviewLayout = () => {
  const { data: user } = useUser();
  const queryClient = useQueryClient();

  const { pathname } = useLocation();

  const createPostDialogHandle = Dialog.useHandle();
  const enableCommunityProfileHandle = Dialog.useHandle();

  const { t } = useTranslation(['forum']);

  return (
    <>
      <ForumHeaderPortal>
        <Header.Root>
          <Header.Content title={t('forum:overview.header.title')} />

          <Header.Actions>
            <Header.Action
              data-pf-id={productFruitsIds.forum.header.createPost}
              icon="Add"
              onClick={() => (user.communityProfile.enabled ? createPostDialogHandle.open(null) : enableCommunityProfileHandle.open(null))}
            >
              {t('forum:createPost.title')}
            </Header.Action>
          </Header.Actions>

          <Header.Tabs>
            <Tabs.Root value={pathname}>
              <Tabs.List>
                <Tabs.TabLink replace to={routes.forumTimeline} value={routes.forumTimeline}>
                  {t('forum:overview.timeline.title')}
                </Tabs.TabLink>

                <Tabs.TabLink
                  onMouseEnter={() => queryClient.prefetchInfiniteQuery(forumSavedPostsQueryOptions)}
                  replace
                  to={routes.forumSavedPosts}
                  value={routes.forumSavedPosts}
                >
                  {t('forum:overview.savedPosts.title')}
                </Tabs.TabLink>

                <Tabs.TabLink
                  onMouseEnter={() => queryClient.prefetchInfiniteQuery(forumUserPostsQueryOptions(user.id))}
                  replace
                  to={routes.forumSelfPosts}
                  value={routes.forumSelfPosts}
                >
                  {t('forum:overview.selfPosts.title')}
                </Tabs.TabLink>
              </Tabs.List>
            </Tabs.Root>
          </Header.Tabs>
        </Header.Root>
      </ForumHeaderPortal>

      <div className={wrapperStyle}>
        <Outlet />
      </div>

      <Dialog.Root handle={createPostDialogHandle}>
        <CreateForumPostDialog onClose={() => createPostDialogHandle.close()} />
      </Dialog.Root>

      <Dialog.Root handle={enableCommunityProfileHandle}>
        <EnableCommunityProfileDialog />
      </Dialog.Root>
    </>
  );
};
